import React, { useCallback, useState } from "react";
import Icon from "@mdi/react";
import { mdiAccountTie, mdiChevronDown, mdiChevronUp, mdiConsole, mdiRobotHappy, mdiShieldCrownOutline } from "@mdi/js";
import { CircularProgress } from "@mui/material";
import { Chat } from "components";
import { useAccount, useTeamMessages, nowTime } from "shared/hooks";
import type { ChatMessage } from "shared/hooks/useTeamMessages";
import styles from "../styles.module.scss";
import { clsx } from "@ismael1361/utils";
import type { PublicTeamDTO, PublicTeamMemberDTO } from "shared/services";
import { ActiveToolsIndicator } from "../components/ActiveToolsIndicator";

interface TeamChatContainerProps {
	workspaceId: string;
	workspaceInternalId: string | number;
	team: PublicTeamDTO;
	members: PublicTeamMemberDTO[];
}

export const TeamChatContainer: React.FC<TeamChatContainerProps> = ({ workspaceId, workspaceInternalId, team, members }) => {
	const user = useAccount();

	const { messages, isStreaming, activeTools, logs, logsEndRef, sendMessage, handleConfirmation, loadingChat, pendingConfirmation } = useTeamMessages(
		{
			workspaceId,
			workspaceInternalId,
			teamId: String(team.id),
			teamInternalId: team.id,
		},
	);

	const [showLogs, setShowLogs] = useState(false);

	const getMemberInfo = useCallback(
		(senderRef?: string) => {
			if (!senderRef) return null;
			return members.find((m) => String(m.id) === senderRef) ?? null;
		},
		[members],
	);

	const getLeaderName = () => (team.name ? `Líder — ${team.name}` : "Líder IA");

	const hasPendingConfirmation = pendingConfirmation !== null;

	const getSenderDisplay = useCallback(
		(msg: ChatMessage): { name: string; avatarUrl?: string; avatarFallbackIconPath?: string } => {
			if (msg.senderType === "user") {
				return { name: user?.firstName || "Você", avatarUrl: user?.profilePictureUrl ?? undefined };
			}
			if (msg.senderType === "member") {
				const member = getMemberInfo(msg.senderRef);
				return { name: member?.name || "Membro IA", avatarUrl: undefined, avatarFallbackIconPath: mdiAccountTie };
			}
			if (msg.senderType === "assistente") {
				return { name: "Assistente", avatarUrl: undefined, avatarFallbackIconPath: mdiRobotHappy };
			}
			return { name: getLeaderName(), avatarUrl: undefined, avatarFallbackIconPath: mdiShieldCrownOutline };
		},
		[user, getMemberInfo, team],
	);

	return (
		<>
			{/* Chat messages area */}
			<div className={styles.chatArea}>
				<Chat
					onSend={sendMessage}
					placeholder="Dê uma ordem para sua equipe ou peça um status..."
					disabled={hasPendingConfirmation}
				>
					{!loadingChat && messages.length === 0 && !isStreaming && (
						<div className={styles.emptyChat}>
							<Icon
								path={mdiShieldCrownOutline}
								size={2.5}
							/>
							<h3>Equipe: {team.name}</h3>
							<p>Envie um comando para o Líder IA da sua equipe. Ele irá coordenar os membros para executar a tarefa.</p>
						</div>
					)}

					{loadingChat && (
						<div className={styles.emptyChat}>
							<CircularProgress
								color="inherit"
								size={24}
							/>
							<span>Carregando conversa...</span>
						</div>
					)}

					{messages.map((msg) => {
						if (msg.senderType === "confirmation" && msg.metadata) {
							return (
								<Chat.Confirmation
									key={msg.id}
									toolName={msg.metadata.toolCall?.name || "tool"}
									description={msg.content || "Deseja executar esta ferramenta?"}
									status={msg.metadata.status || "pending"}
									disabled={isStreaming}
									onConfirm={(approved) => handleConfirmation(msg.metadata!.confirmationId, approved)}
								/>
							);
						}
						const display = getSenderDisplay(msg);
						const outgoing = msg.senderType === "user";
						return (
							<Chat.Message
								key={msg.id}
								id={msg.id}
								incoming={!outgoing}
								outgoing={outgoing}
								avatarUrl={[display.avatarUrl ?? undefined, ...(!outgoing ? [] : [])]}
								avatarFallbackIconPath={display.avatarFallbackIconPath}
								senderName={display.name}
								thinking={msg.thinking}
								pending={msg.pending}
								error={msg.error ? msg.errorMessage || "Ocorreu um erro." : undefined}
								isStreaming={msg.isStreaming}
								attachments={(msg as any).attachments}
								errorAlert={msg.metadata?.error_alert}
							>
								{msg.content || null}
							</Chat.Message>
						);
					})}

					<ActiveToolsIndicator activeTools={activeTools} />
				</Chat>
			</div>

			{/* System Logs panel */}
			<div className={clsx(styles.logsPanel, !showLogs && styles.logsPanelCollapsed)}>
				<button
					className={styles.logsPanelHeader}
					onClick={() => setShowLogs((v) => !v)}
				>
					<Icon
						path={mdiConsole}
						size={0.5}
					/>
					<span>System Logs</span>
					<Icon
						path={showLogs ? mdiChevronDown : mdiChevronUp}
						size={0.6}
						className={styles.logsPanelToggle}
					/>
				</button>
				{showLogs && (
					<div className={styles.logsContent}>
						{logs.length === 0 && (
							<div className={styles.logEntry}>
								<span className={styles.logTime}>[{nowTime()}]</span>
								<span
									className={styles.logSender}
									style={{ color: "#60a5fa" }}
								>
									System
								</span>
								<span className={styles.logText}>Aguardando atividade...</span>
							</div>
						)}
						{logs.map((log, i) => (
							<div
								key={i}
								className={styles.logEntry}
							>
								<span className={styles.logTime}>[{log.time}]</span>
								<span
									className={styles.logSender}
									style={{ color: log.senderColor }}
								>
									{log.sender}
								</span>
								<span className={styles.logText}>{log.text}</span>
							</div>
						))}
						<div ref={logsEndRef} />
					</div>
				)}
			</div>
		</>
	);
};
